import { DataPeriod } from '@prisma/client';
import { UserReportResponse } from '@zond/api-interfaces';
import {
  addDays,
  addHours,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { BarChart3 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AreaChart,
  AreaSeries,
  ChartTooltip,
  Gridline,
  GridlineSeries,
  Line,
  LinearXAxis,
  LinearXAxisTickLabel,
  LinearXAxisTickSeries,
  LinearYAxis,
  LinearYAxisTickLabel,
  LinearYAxisTickSeries,
  TooltipArea,
} from 'reaviz';
import { CHART_COLORS } from '../modules/reports/constants/chart-colors';
import { CustomTooltipContentActivityChart } from './components/CustomTooltipContentActivityChart';

// Props interface
interface ActivityChartToolProps {
  data?: UserReportResponse;
  isLoading?: boolean;
  width?: string | number;
  height?: number;
}

// Data point interface
interface ChartDataPoint {
  key: Date;
  data: number;
}

// Series visibility interface
interface SeriesVisibility {
  clicks: boolean;
  keyPress: boolean;
  mouseActivity: boolean;
}

// Chart data interface
interface ChartData {
  clicksData: ChartDataPoint[];
  keyboardData: ChartDataPoint[];
  mouseData: ChartDataPoint[];
}

// Tooltip data interface
interface TooltipData {
  x: number;
  y: number;
  data: Array<{
    key: string;
    y: number;
    color: string;
  }>;
}

// Series data interface for tooltip
interface SeriesDataItem {
  key: string;
  value: number;
  color: string;
}

export const ActivityChartTool: React.FC<ActivityChartToolProps> = ({
  data,
  isLoading = false,
  width,
  height,
}) => {
  // Get period from URL
  const location = useLocation();
  const [period, setPeriod] = useState<DataPeriod>(DataPeriod.Daily);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Series visibility state
  const [seriesVisibility, setSeriesVisibility] = useState({
    clicks: true,
    keyPress: true,
    mouseActivity: true,
  });

  // Ref for chart container and state for its dimensions
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Update width function
  const updateWidth = useCallback(() => {
    if (chartContainerRef.current?.parentElement) {
      const parentWidth = chartContainerRef.current.parentElement.clientWidth;
      setContainerWidth(parentWidth);
    }
  }, []);

  // Update width on mount, data load, period change and resize
  useEffect(() => {
    // Initial update
    updateWidth();

    // Update after DOM is ready
    requestAnimationFrame(() => {
      updateWidth();
    });

    // Update after a short delay to ensure all layouts are complete
    const timeoutId = setTimeout(() => {
      updateWidth();
    }, 100);

    const handleResize = () => {
      updateWidth();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [updateWidth, data, period]);

  // Update period and dates when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    let periodValue: DataPeriod = DataPeriod.Daily;

    // Check range parameter (Monthly, Weekly, Daily)
    const rangeParam = searchParams.get('range');
    if (rangeParam) {
      if (rangeParam.includes('Monthly')) periodValue = DataPeriod.Monthly;
      else if (rangeParam.includes('Weekly')) periodValue = DataPeriod.Weekly;
      else if (rangeParam.includes('Daily')) periodValue = DataPeriod.Daily;
    }

    setPeriod(periodValue);

    // Set start and end dates based on period
    const now = new Date();
    let start: Date;
    let end: Date = now;

    // Check if there's a date parameter in URL
    const dateParam = searchParams.get('date');
    const dateObj = dateParam ? new Date(dateParam) : now;
    const validDate = isValid(dateObj) ? dateObj : now;

    switch (periodValue) {
      case DataPeriod.Monthly:
        start = startOfMonth(validDate);
        end = endOfMonth(validDate);
        break;
      case DataPeriod.Weekly:
        start = startOfWeek(validDate, { weekStartsOn: 1 });
        end = endOfWeek(validDate, { weekStartsOn: 1 });
        break;
      case DataPeriod.Daily:
      default:
        start = startOfDay(validDate);
        end = endOfDay(validDate);
        break;
    }

    setStartDate(start);
    setEndDate(end);
  }, [location.search]);

  // Process data for chart with proper aggregation
  const chartData = useMemo(() => {
    if (!data?.sessions?.length) {
      return null;
    }

    // Create Map for data aggregation by time intervals
    const timeIntervals = new Map<
      number,
      {
        clicks: number;
        keyPress: number;
        mouseActivity: number;
        count: number;
      }
    >();

    // Determine aggregation interval based on the period
    const getIntervalKey = (date: Date) => {
      switch (period) {
        case DataPeriod.Monthly:
          return startOfDay(date).getTime(); // group by days
        case DataPeriod.Weekly:
          return startOfDay(date).getTime(); // group by days for week
        case DataPeriod.Daily:
        default:
          return startOfHour(date).getTime(); // group by hours for day
      }
    };

    // Get appropriate interval function
    const getNextInterval = (date: Date): Date => {
      switch (period) {
        case DataPeriod.Monthly:
          return addDays(date, 1); // 1 day
        case DataPeriod.Weekly:
          return addDays(date, 1); // 1 day for week
        case DataPeriod.Daily:
        default:
          return addHours(date, 1); // 1 hour for day
      }
    };

    try {
      // First, populate all possible time intervals with zero values
      let currentDate = new Date(startDate);
      const end = new Date(endDate);

      while (currentDate <= end) {
        const intervalKey = getIntervalKey(currentDate);
        timeIntervals.set(intervalKey, {
          clicks: 0,
          keyPress: 0,
          mouseActivity: 0,
          count: 0, // This is now just an activity counter, not used for division
        });
        currentDate = getNextInterval(currentDate);
      }

      // Then aggregate actual data
      let rawActivityPoints = 0;
      data.sessions.forEach((session) => {
        if (!session.SessionActivity) return;

        session.SessionActivity.forEach((activity) => {
          if (!activity?.createdAt) return;

          const date = new Date(activity.createdAt);
          if (isNaN(date.getTime()) || date < startDate || date > endDate) return;

          rawActivityPoints++;
          const interval = getIntervalKey(date);
          const current = timeIntervals.get(interval) || {
            clicks: 0,
            keyPress: 0,
            mouseActivity: 0,
            count: 0,
          };

          timeIntervals.set(interval, {
            clicks: current.clicks + (activity.clicks || 0),
            keyPress: current.keyPress + (activity.keyPress || 0),
            mouseActivity: current.mouseActivity + (activity.mouseActivity || 0),
            count: current.count + 1, // Just tracking the number of activities, not used for averages
          });
        });
      });

      // Transform aggregated data into chart format
      const clicksData: ChartDataPoint[] = [];
      const keyboardData: ChartDataPoint[] = [];
      const mouseData: ChartDataPoint[] = [];

      Array.from(timeIntervals.entries())
        .sort(([a], [b]) => a - b)
        .forEach(([time, data]) => {
          // Use sum values instead of averages
          clicksData.push({
            key: new Date(time),
            data: data.clicks, // No longer dividing by count
          });
          keyboardData.push({
            key: new Date(time),
            data: data.keyPress, // No longer dividing by count
          });
          mouseData.push({
            key: new Date(time),
            data: data.mouseActivity, // No longer dividing by count
          });
        });

      const result = { clicksData, keyboardData, mouseData };

      return result;
    } catch (error) {
      console.error('Error processing chart data:', error);
      return null;
    }
  }, [data, startDate, endDate, period]);

  // Format time label for X axis
  const formatXAxisTime = (value: any): string => {
    if (typeof value !== 'number') {
      console.error('Invalid data type for X axis formatting:', value);
      return '';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', value);
      return '';
    }

    switch (period) {
      case DataPeriod.Monthly:
        return format(date, 'dd.MM');
      case DataPeriod.Weekly:
        return format(date, 'EEE dd.MM');
      case DataPeriod.Daily:
        return format(date, 'HH:mm');
      default:
        return '';
    }
  };

  // Generate time marks for X axis
  const generateTimeMarks = useMemo(() => {
    const marks: Date[] = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);

    // Determine label interval based on period
    const addInterval = (date: Date): Date => {
      switch (period) {
        case DataPeriod.Monthly:
          return addDays(date, 1); // 1 day
        case DataPeriod.Weekly:
          return addDays(date, 1); // 1 day for week
        case DataPeriod.Daily:
          return addHours(date, 1); // 1 hour for day
      }
    };

    while (currentDate <= end) {
      marks.push(new Date(currentDate));
      currentDate = addInterval(currentDate);
    }

    // Add end point if it doesn't exist
    if (marks[marks.length - 1] < end) {
      marks.push(end);
    }

    // Filter duplicate labels to avoid the same day/hour appearing multiple times
    const uniqueLabels = new Set<string>();
    const filteredMarks = marks.filter((date) => {
      const label = formatXAxisTime(date.getTime());
      if (uniqueLabels.has(label)) {
        return false;
      }
      uniqueLabels.add(label);
      return true;
    });

    return filteredMarks;
  }, [startDate, endDate, period, formatXAxisTime]);

  // Get visible series with normalized data
  const visibleSeries = useMemo(() => {
    if (!chartData) return [];

    const result = [];

    if (seriesVisibility.clicks) {
      result.push({
        key: 'Clicks',
        data: chartData.clicksData,
        colorScheme: [CHART_COLORS.activity.clicks],
      });
    }

    if (seriesVisibility.keyPress) {
      result.push({
        key: 'Key Press',
        data: chartData.keyboardData,
        colorScheme: [CHART_COLORS.activity.keyPress],
      });
    }

    if (seriesVisibility.mouseActivity) {
      result.push({
        key: 'Mouse Activity',
        data: chartData.mouseData,
        colorScheme: [CHART_COLORS.activity.mouseActivity],
      });
    }

    return result;
  }, [chartData, seriesVisibility]);

  // Toggle series visibility
  const toggleSeries = (series: keyof SeriesVisibility) => {
    setSeriesVisibility((prev) => {
      // Check if this series is the last active one
      const activeSeriesCount = Object.values(prev).filter(Boolean).length;
      const isLastActiveSeries = activeSeriesCount === 1 && prev[series] === true;

      // If trying to disable the last active series, enable all series
      if (isLastActiveSeries) {
        return {
          clicks: true,
          keyPress: true,
          mouseActivity: true,
        };
      }

      // Otherwise toggle state as usual
      return {
        ...prev,
        [series]: !prev[series],
      };
    });
  };

  // Format value for Y axis
  const formatYAxisValue = (value: number): string => {
    if (value >= 1000) return `${Math.floor(value / 1000)}k`;
    return Math.floor(value).toString();
  };

  // Get period name for title
  const getPeriodName = (): string => {
    switch (period) {
      case DataPeriod.Monthly:
        return 'month';
      case DataPeriod.Weekly:
        return 'week';
      case DataPeriod.Daily:
      default:
        return 'day';
    }
  };

  // Format date for tooltip
  const formatTooltipDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (period) {
      case DataPeriod.Monthly:
      case DataPeriod.Weekly:
        return `${day}.${month}.${year}`;
      case DataPeriod.Daily:
      default:
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
  };

  // If data is loading
  if (isLoading) {
    return (
      <div className="w-full h-[350px] flex items-center justify-center">
        <div className="text-gray-500">Loading data...</div>
      </div>
    );
  }

  // If no data
  if (!chartData) {
    return (
      <div className="w-full h-[384px] flex flex-col items-center justify-center text-center">
        <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
          <BarChart3 className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Activity Data</h3>
        <p className="text-muted-foreground">
          Activity data will appear here once
          <br />
          users start tracking their activity.
        </p>
      </div>
    );
  }

  // Render component
  return (
    <div className="w-full h-[384px] bg-card">
      {/* Legend */}
      <div className="w-full h-[60px] px-4 py-2 flex justify-end items-center">
        <div className="flex gap-4 bg-card border rounded-md px-4">
          <div
            className="flex items-center cursor-pointer px-2 py-2"
            onClick={() => toggleSeries('clicks')}
            style={{ opacity: seriesVisibility.clicks ? 1 : 0.5 }}
          >
            <div className="w-5 h-5 mr-2 rounded" style={{ backgroundColor: CHART_COLORS.activity.clicks }} />
            <span className="text-sm font-medium">Clicks</span>
          </div>
          <div
            className="flex items-center cursor-pointer px-2 py-2"
            onClick={() => toggleSeries('keyPress')}
            style={{ opacity: seriesVisibility.keyPress ? 1 : 0.5 }}
          >
            <div
              className="w-5 h-5 mr-2 rounded"
              style={{ backgroundColor: CHART_COLORS.activity.keyPress }}
            />
            <span className="text-sm font-medium">Key Press</span>
          </div>
          <div
            className="flex items-center cursor-pointer px-2 py-2"
            onClick={() => toggleSeries('mouseActivity')}
            style={{ opacity: seriesVisibility.mouseActivity ? 1 : 0.5 }}
          >
            <div
              className="w-5 h-5 mr-2 rounded"
              style={{ backgroundColor: CHART_COLORS.activity.mouseActivity }}
            />
            <span className="text-sm font-medium">Mouse Activity</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div ref={chartContainerRef} className="w-full h-[324px]" style={{ minWidth: '100%' }}>
        {containerWidth > 0 && (
          <AreaChart
            width={containerWidth}
            height={324}
            data={visibleSeries}
            margins={[20, 0, 20, 30]}
            series={
              <AreaSeries
                type="stacked"
                interpolation="linear"
                colorScheme={[
                  CHART_COLORS.activity.clicks,
                  CHART_COLORS.activity.keyPress,
                  CHART_COLORS.activity.mouseActivity,
                ]}
                line={<Line strokeWidth={2} />}
                tooltip={
                  <TooltipArea
                    tooltip={
                      <ChartTooltip
                        followCursor={true}
                        content={(data: any, color: string) => (
                          <CustomTooltipContentActivityChart data={data} color={color} />
                        )}
                      />
                    }
                  />
                }
              />
            }
            gridlines={<GridlineSeries line={<Gridline strokeWidth={0.5} strokeDasharray="4 4" />} />}
            xAxis={
              <LinearXAxis
                type="time"
                domain={[startDate.getTime(), endDate.getTime()]}
                tickSeries={
                  <LinearXAxisTickSeries
                    label={
                      <LinearXAxisTickLabel
                        format={formatXAxisTime}
                        rotation={period === DataPeriod.Monthly ? -45 : 0}
                        textAnchor={period === DataPeriod.Monthly ? 'end' : 'middle'}
                        fontSize={11}
                        fill="#333"
                        padding={0}
                        align="center"
                        position="end"
                      />
                    }
                    tickSize={5}
                    tickValues={generateTimeMarks.map((date) => date.getTime())}
                  />
                }
                visibility="visible"
                axisLine={null}
              />
            }
            yAxis={
              <LinearYAxis
                type="value"
                axisLine={null}
                tickSeries={
                  <LinearYAxisTickSeries
                    label={
                      <LinearYAxisTickLabel format={formatYAxisValue} fontSize={11} fill="#333" padding={0} />
                    }
                  />
                }
              />
            }
          />
        )}
      </div>
    </div>
  );
};
